import { css } from "@emotion/css";
import {
  Breadcrumb,
  Button,
  Col,
  Drawer,
  FloatButton,
  Layout,
  Row,
  Tabs,
} from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import ContactSupport from "../../Global/Pages/ContactSupport";
import AppHeaderSimple from "./appHeaderSimple";
import ElementIcon from "../../Theme/CustomIcons/Icons/Grid/ElementIcon";
import CalculatorIcon from "../../Theme/CustomIcons/Icons/School,Learning/CalculatorIcon";
import { TranslatorNavigationTag } from "../../languageTranslator/LanguageTranslatorTags";
import BrifeCaseIcon from "../../Theme/CustomIcons/Icons/School,Learning/BrifeCaseIcon";
import BuildingsIcon from "../../Theme/CustomIcons/Icons/Building/BuildingsIcon";
import TimerIcon from "../../Theme/CustomIcons/Icons/Time/TimerIcon";
import GearIcon from "../../Theme/CustomIcons/Icons/Settings/GearIcon";
import { Link } from "react-router-dom";
import SettingsIcon from "../../Icons/SettingsIcon";
import DocumentTexIcon from "../../Theme/CustomIcons/Icons/ContentEdit/DocumentTextIcon";
import SupportWidget from "../../Global/SupportWidget/SupportWidget";
import CloseIcon from "../../Theme/CustomIcons/Icons/CustomIcons/CloseIcon";

const fullPageContainer = css`
  display: flex;
  justify-content: center;
  width: 100%;
`;

function FullPageLayout({
  name,
  callGetApi,
  fromOnboarding,
  children,
  settingLink,
  settings,
  settingsWidth,
  settingsTitle,
  username,
  breadcrumbItems,
}) {
  const breadCrumbsList = useSelector((state) => state.breadCrumbsList);

  const bread1Array = [
    {
      id: "dashboard",
      icon: <ElementIcon />,
      label: <TranslatorNavigationTag>Dashboard</TranslatorNavigationTag>,
    },
    {
      id: "time",
      icon: <TimerIcon />,
      label: <TranslatorNavigationTag>Time</TranslatorNavigationTag>,
    },
    {
      id: "payroll",
      icon: <CalculatorIcon />,
      label: <TranslatorNavigationTag>Payroll</TranslatorNavigationTag>,
    },
    {
      id: "recruitment",
      icon: <BrifeCaseIcon />,
      label: <TranslatorNavigationTag>Grow</TranslatorNavigationTag>,
    },
    {
      id: "training",
      icon: <DocumentTexIcon />,
      label: <TranslatorNavigationTag>Training</TranslatorNavigationTag>,
    },
    {
      id: "report",
      icon: <DocumentTexIcon />,
      label: <TranslatorNavigationTag>Report</TranslatorNavigationTag>,
    },
    {
      id: "company",
      icon: <BuildingsIcon />,
      label: <TranslatorNavigationTag>Company</TranslatorNavigationTag>,
    },
    {
      id: "settings",
      icon: <GearIcon size="small" />,
      label: <TranslatorNavigationTag>Settings</TranslatorNavigationTag>,
    },
    {
      id: "support",
      label: <TranslatorNavigationTag>Support Center</TranslatorNavigationTag>,
    },
  ];

  const excludedBreadcrumbs = [
    "dashboard",
    "report",
    "theme settings",
    "user profile",
    "profile settings",
    "announcement",
    "training",
    "organization settings",
    "qr code",
    "create new employee",
    "edit employee",
  ];

  const breads = [
    {
      title:
        !excludedBreadcrumbs.includes(name?.toLowerCase()) &&
        bread1Array.map((_b) => {
          if (_b.id.toLowerCase() === breadCrumbsList.info) {
            return (
              <div
                style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                <span>{_b.label}</span>
              </div>
            );
          }
        }),
    },
    {
      title: !excludedBreadcrumbs.includes(name?.toLowerCase()) && (
        <TranslatorNavigationTag>{name}</TranslatorNavigationTag>
      ),
    },
  ];

  const userProfileBread = [
    {
      title: (
        <div
          className={css`
            text-transform: uppercase;
          `}
          style={{ display: "flex", gap: "5px", alignItems: "center" }}>
          <TranslatorNavigationTag>{name}</TranslatorNavigationTag>
        </div>
      ),
    },
    {
      title: username,
    },
  ];

  const createUserProfileBread = [
    {
      title: (
        <div
          className={css`
            text-transform: uppercase;
          `}
          style={{ display: "flex", gap: "5px", alignItems: "center" }}>
          <TranslatorNavigationTag>Employee</TranslatorNavigationTag>
        </div>
      ),
    },
    {
      title: (
        <div
          className={css`
            text-transform: uppercase;
          `}
          style={{ display: "flex", gap: "5px", alignItems: "center" }}>
          <TranslatorNavigationTag>{name}</TranslatorNavigationTag>
        </div>
      ),
    },
    ...(username
      ? [
        {
          title: username,
        },
      ]
      : []),
  ];

  const getLanguageTranslatorList = useSelector(
    (state) => state.getLanguageTranslatorList,
  );
  const { translations } = getLanguageTranslatorList;
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const handleSettingsDrawerClose = () => setSettingsDrawerOpen(false);
  const handleSettingsDrawerOpen = () => setSettingsDrawerOpen(true);

  const sharedLogin = useSelector((state) => state.sharedLogin);
  const { sharedUserInfo } = sharedLogin;
  const themeDefault = useSelector((state) => state.themeDefault);
  let userType = "";
  if (sharedUserInfo?.payload?.type) {
    userType = sharedUserInfo.payload.type;
  } else if (sharedUserInfo?.type) {
    userType = sharedUserInfo.type;
  }

  const centeredPage = css`
    margin-top: clamp(20px, 4vw, 50px);
    margin-bottom: 25px;
    max-width: ${userType === "Kiosk"
      ? "clamp(600px, 95vw, 1400px)"
      : "clamp(600px, 80vw, 1400px)"};
  `;

  const token =
    JSON.parse(localStorage.getItem("sharedUserInfo")) ||
    sharedUserInfo?.payload?.token;

  const getBreadcrumbItems = () => {
    // If custom breadcrumbItems provided, use them
    if (breadcrumbItems) {
      return breadcrumbItems;
    }

    // Otherwise, use existing logic
    return excludedBreadcrumbs.includes(name?.toLowerCase())
      ? username
        ? userProfileBread
        : name?.toLowerCase() === "create new employee"
          ? createUserProfileBread
          : name?.toLowerCase() === "edit employee"
            ? createUserProfileBread
            : name?.toLowerCase() === "dashboard" ||
              name?.toLowerCase() === "report"
              ? null
              : [{ title: name, key: name }]
      : breads;
  };

  const finalBreadcrumbItems = getBreadcrumbItems();

  return (
    <Layout color="primary" style={{ height: "100% !important" }}>
      <div className="overall-background-change">
        <AppHeaderSimple
          name={name}
          fromOnboarding={fromOnboarding}
          callGetApi={callGetApi}
        />
        <Row className={fullPageContainer}>
          <Layout className={centeredPage}>
            <div
              className={css`
                .ant-breadcrumb ol {
                  display: flex;
                  align-items: center;
                  text-transform: uppercase;
                }
                margin: ${name?.toLowerCase() === "dashboard" ||
                  name?.toLowerCase() === "report"
                  ? "12px 0px 12px"
                  : "clamp(26px,2vw,31px) 0 24px"};

                align-items: center;
                gap: 5px;
                display: flex;
                justify-content: space-between;
              `}>
              <div style={{ display: "flex" }}>
                {/* ✅ Use finalBreadcrumbItems instead of inline logic */}
                {finalBreadcrumbItems && (
                  <Breadcrumb
                    style={{
                      fontSize:
                        translations?.direction === "rtl" ? "14px" : "12px",
                    }}
                    items={finalBreadcrumbItems}
                  />
                )}
              </div>
              <div>
                {settings && settings.length > 0 && (
                  <div>
                    <p
                      onClick={handleSettingsDrawerOpen}
                      style={{
                        display: "flex",
                        gap: "6px",
                        cursor: "pointer",
                        alignItems: "center",
                        color: themeDefault?.info?.newConfig?.color,
                      }}>
                      <span>Manage Settings</span>
                      <GearIcon size="small" />
                    </p>
                  </div>
                )}
              </div>
            </div>
            {children}
          </Layout>
          {settings && settings.length > 0 && (
            <Drawer
              width={settingsWidth || "100%"}
              open={settingsDrawerOpen}
              onClose={handleSettingsDrawerClose}
              closeIcon={false}
              title={
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: "500" }}>{settingsTitle}</span>
                  <p
                    onClick={handleSettingsDrawerClose}
                    style={{ cursor: "pointer" }}>
                    <CloseIcon />
                  </p>
                </div>
              }>
              {settings && settings.length > 1 ? (
                <Tabs
                  tabPosition="left"
                  items={settings}
                  defaultActiveKey={settings[0]?.key}
                  style={{ height: "calc(100vh - 240px)" }}
                  className={css`
                    .ant-card {
                      box-shadow: none;
                    }
                  `}
                />
              ) : (
                settings.map((set) => (
                  <div
                    className={css`
                      .ant-card {
                        box-shadow: none;
                      }
                    `}
                    key={set.key}>
                    {set?.children}
                  </div>
                ))
              )}
            </Drawer>
          )}
          <SupportWidget token={token} />
        </Row>
      </div>
    </Layout>
  );
}

export default FullPageLayout;
